import {gql} from '@apollo/client';

import {useMultipassUrlQueryLazyQuery} from '#apollo/schema';
import {ECOMMERCE_URL, MEMBERSHIP_URL} from '#constant/ecommerce';
import {useAuth} from '#hook';

export const multipassUrlQuery = gql`
  query MultipassUrlQuery($returnUrl: String!) {
    me {
      id
      multipassUrl(returnUrl: $returnUrl)
    }
  }
`;

/*
 * Used to generate a link to Shopify with authentication maintained.
 * If URL generation fails for any reason, the original URL is returned instead.
 * This provides a similar experience, without any auth.
 *
 * By default uses the NEXT_PUBLIC_STORE_URL and NEXT_PUBLIC_STORE_KIT_URL
 * env vars concatenated together. Can be overridden by passing in a different
 * value.
 */
export const useMultipassUrl = () => {
  const {isUserSignedIn} = useAuth();
  const [getMultipassUrlQuery] = useMultipassUrlQueryLazyQuery({
    variables: {returnUrl: ''},

    // Multipass URL expires in 15 minutes, so we basically never want to cache
    // them and should always re-queried when needed.
    fetchPolicy: 'no-cache',
  });

  const getMultipassUrl = async (
    shopUrl: string = `${ECOMMERCE_URL}/${MEMBERSHIP_URL}`,
    shopUrlParams = {},
  ) => {
    let returnUrl = shopUrl;
    if (Object.keys(shopUrlParams).length) {
      returnUrl = `${shopUrl}?${new URLSearchParams(shopUrlParams).toString()}`;
    }

    const isSignedIn = await isUserSignedIn({async: true});
    if (isSignedIn) {
      try {
        const {data} = await getMultipassUrlQuery({variables: {returnUrl}});
        const {multipassUrl} = data?.me || {};

        return multipassUrl || returnUrl;
      } catch (error) {
        console.error(error);
        return returnUrl;
      }
    }

    return returnUrl;
  };

  return {
    getMultipassUrl,
  };
};
